import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  TextField,
  Paper,
  Typography,
  Avatar,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Link
} from "@mui/material";
import ConversationGateway from '../../../gateway/conversation/ConversationGateway';
import ReactMarkdown from 'react-markdown';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Autocomplete from '@mui/material/Autocomplete';

const ChatContainer = styled(Box)(({ theme }) => ({
  height: "80vh",
  width: "98vw",
  display: "flex",
  flexDirection: "column",
  position: "relative",
  borderTopLeftRadius: '4px',
  borderTopRightRadius: '4px',
  borderBottomLeftRadius: '30px',
  borderBottomRightRadius: '30px',
  background: 'linear-gradient(124deg, rgba(9,9,121,1) 0%, rgba(0,80,255,1) 33%, rgba(0,215,37,0.9) 100%)'
}));

const MessagesContainer = styled(Paper)(({ theme }) => ({
  flex: 1,
  overflowY: "auto",
  padding: theme.spacing(2),
  paddingBottom: theme.spacing(10),
  backgroundColor: "rgba(255,255,255,0.6)",
  borderBottomLeftRadius: '0px',
  borderBottomRightRadius: '0px',
}));

const MessageBubble = styled(Box)(({ theme, isuser }) => ({
  display: "flex",
  alignItems: "flex-start",
  marginBottom: theme.spacing(2),
  flexDirection: isuser ? "row-reverse" : "row",
  gap: theme.spacing(1)
}));

const MessageContent = styled(Paper)(({ theme, isuser }) => ({
  padding: theme.spacing(1.5),
  maxWidth: "70%",
  backgroundColor: isuser ? theme.palette.primary.main : theme.palette.grey[200],
  color: isuser ? theme.palette.primary.contrastText : theme.palette.text.primary,
  borderRadius: theme.spacing(2),
  "& .markdown": {
    "& p": {
      margin: 0
    },
    "& strong": {
      fontWeight: 600
    },
    "& em": {
      fontStyle: "italic"
    },
    "& code": {
      backgroundColor: "rgba(0, 0, 0, 0.1)",
      padding: "2px 4px",
      borderRadius: "4px"
    },
    "& blockquote": {
      borderLeft: "4px solid",
      borderColor: isuser ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.2)",
      paddingLeft: theme.spacing(1),
      margin: theme.spacing(1, 0)
    }
  }
}));

const InputContainer = styled(Box)(({ theme }) => ({
  left: 0,
  right: 0,
  display: "flex",
  gap: theme.spacing(1),
  padding: theme.spacing(2),
  backgroundColor: "rgba(255, 255, 255, 0.9)",
  backdropFilter: "blur(10px)",
  borderTop: `1px solid ${theme.palette.divider}`,
  borderBottomLeftRadius: '4px',
  borderBottomRightRadius: '4px',
  zIndex: 1000
}));

// const InputContainer = styled(Box)(({ theme }) => ({
//   position: "fixed",
//   bottom: 0,
//   left: 0,
//   right: 0,
//   display: "flex",
//   gap: theme.spacing(1),
//   padding: theme.spacing(2),
//   backgroundColor: "rgba(255, 255, 255, 0.9)",
//   backdropFilter: "blur(10px)",
//   borderTop: `1px solid ${theme.palette.divider}`,
//   zIndex: 1000
// }));

const MessageImage = styled("img")({
  maxWidth: "100%",
  borderRadius: "8px",
  marginTop: "8px"
});

const ConversationInterface = ({config, searchText, handleSearchChange, criteria, handleCriteriaChange, setPrompt, setChainOfThoughtReasoning, setLLMResponse}) => {
  const gateway = new ConversationGateway({ config });
  const [suggestions, setSuggestions] = useState([]);
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Hello, my name is Janis! I am an intelligent consumer research guidance assistant. What vehicle are you interested in exploring?",
      sender: "responder",
      timestamp: new Date().toLocaleTimeString(),
    },
  ]);
  const [newMessage, setNewMessage] = useState("");
  const [llmMessages, setLLMMessages] = useState([{
    content: "Hello, my name is Janis! I am an intelligent consumer research guidance assistant. What vehicle are you interested in exploring?",
    role: "assistant"
  }]);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    //   const delay = setTimeout(() => {
    //     if(searchText) {
    //         const fetchSuggestions = async () => {
    //             try {
    //                 const response = await axios.get(config.SuggestionAPI.url + `?search=${searchText}`);
    //                 setSuggestions(response.data);
    //             } catch (error) {
    //                 console.error('Error fetching suggestions:', error);
    //             }
    //         };
    //         fetchSuggestions();
    //     } else {
    //         setSuggestions([]);
    //     }
    //   }, 1000);

    // return () => clearTimeout(delay);
  }, [searchText, config.SuggestionAPI.url]);


  const handleSend = async () => {
    if (newMessage.trim()) {
        const message = {
            id: messages.length + 1,
            text: newMessage.replace(/(https?:\/\/[^\s]+)/g, '[$1]($1)'),
            sender: "user",
            timestamp: new Date().toLocaleTimeString()
        };
        let newMessages = [...messages, message];
        setMessages(newMessages);
        setNewMessage("");
        const llmMessage = {
          content: newMessage,
          role: "user"
        };
        let newLLMMessages = [...llmMessages, llmMessage];
        setLLMMessages(newLLMMessages);
        setPrompt(newLLMMessages);

        handleSearchChange(null, '');

        let responseMessage = '';
        
        const responderMessage = {
          id: messages.length + 2,
          text: '...',
          sender: "responder",
          timestamp: new Date().toLocaleTimeString()
        };
        setMessages(prev => [...prev, responderMessage]);

        await gateway.chat(newLLMMessages, (chunk) => {
            responseMessage += chunk.content;
            
            let msg = responseMessage
              .replace('{|COTR_START|}', '')
              .replace('{|COTR_END|}', '')
              .replace(/(https?:\/\/[^\s]+)/g, "[$1]($1)");
            
            setMessages(prev => {
              const updateResponseMessage = [...prev];
              updateResponseMessage[updateResponseMessage.length - 1] = {
                ...updateResponseMessage[updateResponseMessage.length - 1],
                text: msg
              };
              return updateResponseMessage;
            });
        });

        setLLMResponse(responseMessage.replace(/({\|COTR_START\|}.*{\|COTR_END\|})/g, ''));
        setChainOfThoughtReasoning(responseMessage.replace(/.*{\|COTR_START\|}(.*){\|COTR_END\|}.*/g, '$1'));
        //handleCriteriaChange(response);
        
        const llmResponseMessage = {
          content: responseMessage,
          role: "assistant"
        };
        setLLMMessages([...llmMessages, llmResponseMessage]);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <ChatContainer>
      <MessagesContainer elevation={3}>
        {messages.map((message) => (
          <MessageBubble key={message.id} isuser={message.sender === "user" ? "true" : undefined}>
            <Avatar
              src={message.sender === "user" ? "./bernardo-crop.png" : "./avatar.png"}
              alt={message.sender}
            />
            <MessageContent isuser={message.sender === "user" ? "true" : undefined} elevation={1}>
              <Typography variant="subtitle2" fontWeight="bold">
                {message.sender === "user" ? "Bernardo" : "Janis"}
              </Typography>
              <Box variant="markdown">
                <ReactMarkdown>{message.text}</ReactMarkdown>
              </Box>
              {message.image && (
                <MessageImage src={message.image} alt="Message attachment" />
              )}
              {message.table && (
                <TableContainer component={Paper} sx={{ mt: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Product</TableCell>
                        <TableCell align="right">Sales</TableCell>
                        <TableCell align="right">Revenue</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {message.table.map((row, index) => (
                        <TableRow key={index}>
                          <TableCell>{row.product}</TableCell>
                          <TableCell align="right">{row.sales}</TableCell>
                          <TableCell align="right">{row.revenue}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              {message.link && (
                <Link href={message.link} target="_blank" sx={{ display: "block", mt: 1 }}>
                  View Location Details
                </Link>
              )}
              <Typography variant="caption" display="block" sx={{ opacity: 0.7, mt: 1 }}>
                {message.timestamp}
              </Typography>
            </MessageContent>
          </MessageBubble>
        ))}
        <div ref={messagesEndRef} />
      </MessagesContainer>

      <InputContainer>
        <Autocomplete
          freeSolo
          options={suggestions}
          value={searchText}
          forcePopupIcon={false}
          fullWidth
          disableClearable
          onInputChange={(event, value) => handleSearchChange(event, value)}
          getOptionLabel={(option) => typeof option === 'string' || option instanceof String ? option : option.value}
          filterOptions={x => x}
          renderInput={(params) => (
            <TextField
              {...params} 
              fullWidth
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              label="Search with AI - Built with Llama ..."
              variant="outlined"
              size="small"
              InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                      <InputAdornment position="end">
                          <IconButton disabled={!newMessage.trim()} onClick={(event) => handleSend(event, newMessage)}>
                              <img src="/search-button.png" alt="search" style={{ width: '43px', height: '43px', marginRight: '-16px'}} />
                          </IconButton>
                      </InputAdornment>
                  ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                    'fieldset': {
                      borderWidth: '2px',
                    },
                    '&:hover fieldset': {
                        borderColor: '#308BEE',
                        borderWidth: '2px'
                    },
                    '&.Mui-focused fieldset': {
                        borderColor: '#308BEE',
                        borderWidth: '2px'
                    },
                },
            }}
            />
          )}
        />
      </InputContainer>
    </ChatContainer>
  );
};

export default ConversationInterface;